import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { getPresignedUrl, uploadFileToS3 } from '@/api/file';

interface UseUploadDocumentOptions {
  destinationPath: string;
  onUpdate: (url: string | null) => Promise<unknown>;
  invalidateKey: (string | number)[];
  showToast?: boolean;
}

export function useUploadDocument({
  destinationPath,
  onUpdate,
  invalidateKey,
  showToast = true,
}: UseUploadDocumentOptions) {
  const queryClient = useQueryClient();

  const uploadMutation = useMutation<
    { downloadUrl: string; url: string },
    Error,
    File
  >({
    mutationFn: async (file: File) => {
      const presigned = await getPresignedUrl({
        filename: file.name,
        size: file.size || undefined,
        destination: destinationPath,
      });

      await uploadFileToS3(file, presigned.uploadUrl);
      await onUpdate(presigned.url);

      return { downloadUrl: presigned.downloadUrl, url: presigned.url };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invalidateKey });
      if (showToast) toast.success('Document uploaded!');
    },
    onError: () => {
      if (showToast) toast.error('Failed to upload document');
    },
  });

  return {
    uploadDocument: uploadMutation.mutateAsync,
    isUploading: uploadMutation.isPending,
  };
}
